import React, { useState } from 'react';
import { FiArrowLeft, FiAlertTriangle, FiCheckCircle } from 'react-icons/fi';
import './PestDiseasesPage.css';

const ALL_DISEASES = [
  {
    id: 1,
    name: "Late Blight",
    type: "Fungal (Oomycete)",
    symptoms: "Dark, water-soaked spots on leaves that turn brown/black, white fungal growth under humid conditions, rot in tubers/fruits.",
    solution: "BIO-ORBI Super Novel (Foliar supplement to enhance plant cellular resistance and recover crop vitality).",
    img: "/diseases/late_blight.png",
    category: "Foliar Blights"
  },
  {
    id: 2,
    name: "Powdery Mildew",
    type: "Fungal",
    symptoms: "White to gray powdery spots or patches on the upper surface of leaves, stems, and buds, causing leaf curling and yellowing.",
    solution: "BIO-ORBI Plus (Advanced formulation that boosts leaf nutrient density and systemic crop defense).",
    img: "/diseases/powdery_mildew.png",
    category: "Mildews"
  },
  {
    id: 3,
    name: "Leaf Spot",
    type: "Fungal / Bacterial",
    symptoms: "Small, circular brown or black spots with concentric rings or yellow halos on foliage, leading to premature leaf drop.",
    solution: "BIO-ORBI Plus (Strengthens plant immunity and optimizes chlorophyll production under disease stress).",
    img: "/diseases/leaf_spot.png",
    category: "Foliar Spots"
  },
  {
    id: 4,
    name: "Aphids",
    type: "Insect Pest",
    symptoms: "Clusters of tiny, soft-bodied green or black bugs under leaves, sticky honeydew residues, curled leaves, and stunted stems.",
    solution: "BIO-ORBI Prime (Liquid biofertilizer that stimulates healthy, dense sap flow and natural plant vigor to withstand pest pressure).",
    img: "/diseases/aphids.png",
    category: "Pests & Vectors"
  },
  {
    id: 5,
    name: "Root Rot",
    type: "Fungal (Pythium / Phytophthora)",
    symptoms: "Yellowing and wilting of the canopy, stunted growth, roots appear dark brown or black, soft, slimy, and decaying.",
    solution: "BIO-ORBI Vermicompost + Super Novel (Rebuilds the root microbiome, improves aeration, and stimulates healthy root cell regeneration).",
    img: "/diseases/root_rot.png",
    category: "Root Diseases"
  },
  {
    id: 6,
    name: "Rust",
    type: "Fungal",
    symptoms: "Powdery orange, yellow, or rust-colored pustules on the undersides of leaves, resulting in yellowing and leaf drying.",
    solution: "BIO-ORBI Plus (Foliar booster designed to reinforce cellular walls and optimize general crop metabolism).",
    img: "/diseases/rust.png",
    category: "Fungus & Rusts"
  },
  {
    id: 7,
    name: "Downy Mildew",
    type: "Fungal (Oomycete)",
    symptoms: "Angular yellow patches on leaf tops bounded by veins, with gray, downy spore growth underneath in humid conditions.",
    solution: "BIO-ORBI Plus (Supports robust leafy canopy growth and enhances plant defense pathways).",
    img: "/diseases/downy_mildew.png",
    category: "Mildews"
  },
  {
    id: 8,
    name: "Anthracnose",
    type: "Fungal",
    symptoms: "Sunken, dark circular spots or lesions on fruits, stems, and leaves, often developing pinkish gelatinous spore masses.",
    solution: "BIO-ORBI Plus (Helps crops recover and maintain post-flowering fruit quality and integrity).",
    img: "/diseases/anthracnose.png",
    category: "Foliar Blights"
  },
  {
    id: 9,
    name: "Black Spot",
    type: "Fungal",
    symptoms: "Circular black spots with feathery edges on leaves, starting from lower foliage and causing rapid yellowing and leaf drop.",
    solution: "BIO-ORBI Super Novel (Stimulates new foliar flushes and fortifies plant surfaces).",
    img: "/diseases/black_spot.png",
    category: "Foliar Spots"
  },
  {
    id: 10,
    name: "Spider Mites",
    type: "Insect Pest",
    symptoms: "Fine webbing on leaves and stems, yellow stippling or specks on foliage, leading to bronze-colored drying leaves.",
    solution: "BIO-ORBI Prime (Enhances root vigor and water absorption to maintain turgidity under pest stress).",
    img: "/diseases/spider_mites.png",
    category: "Pests & Vectors"
  },
  {
    id: 11,
    name: "Whiteflies",
    type: "Insect Pest",
    symptoms: "Tiny white moth-like insects that flutter when disturbed, sucking sap, producing honeydew, and transmitting crop viruses.",
    solution: "BIO-ORBI Prime (Promotes natural defense compound secretion and crop density).",
    img: "/diseases/whiteflies.png",
    category: "Pests & Vectors"
  },
  {
    id: 12,
    name: "Fusarium Wilt",
    type: "Fungal",
    symptoms: "Drooping leaf tips, yellowing of leaves starting from the bottom up (often on one side), vascular browning inside the stem.",
    solution: "BIO-ORBI Vermicompost (Inoculates the root zone with beneficial organic microorganisms that block fungal entry).",
    img: "/diseases/fusarium_wilt.png",
    category: "Root Diseases"
  }
];

export default function PestDiseasesPage({ onBack, triggerNotification }) {
  const [selectedCategory, setSelectedCategory] = useState('all');

  const categories = ['all', 'Foliar Blights', 'Mildews', 'Foliar Spots', 'Pests & Vectors', 'Root Diseases', 'Fungus & Rusts'];

  const filteredDiseases = selectedCategory === 'all'
    ? ALL_DISEASES
    : ALL_DISEASES.filter(d => d.category === selectedCategory);

  return (
    <div className="pdp-root">
      {/* Page Header */}
      <div className="pdp-header">
        <div className="container pdp-header-inner">
          <button className="pdp-back-btn" onClick={onBack}>
            <FiArrowLeft size={16} /> Back to Store
          </button>
          <h1 className='crop'>Crop Disease & Pest Directory</h1>
          <p>Learn to identify common crop issues and discover organic treatments to protect your farm naturally.</p>

          {/* Category Filters */}
          <div className="pdp-filters">
            {categories.map(cat => (
              <button
                key={cat}
                className={`pdp-filter-btn ${selectedCategory === cat ? 'active' : ''}`}
                onClick={() => setSelectedCategory(cat)}
              >
                {cat === 'all' ? 'All Diseases' : cat}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Directory Grid */}
      <div className="container pdp-body">
        <div className="pdp-grid">
          {filteredDiseases.map(disease => (
            <div key={disease.id} className="pdp-card">
              {disease.img ? (
                <div className="pdp-card-img-box">
                  <img src={disease.img} alt={disease.name} className="pdp-card-img" />
                </div>
              ) : (
                <div className="pdp-card-placeholder">
                  <FiAlertTriangle size={32} className="pdp-placeholder-icon" />
                  <span>No Photo Available</span>
                </div>
              )}

              <div className="pdp-card-content">
                <div className="pdp-card-badge-row">
                  <span className="pdp-badge type">{disease.type}</span>
                  <span className="pdp-badge category">{disease.category}</span>
                </div>

                <h2>{disease.name}</h2>

                <div className="pdp-section">
                  <h3>Symptoms</h3>
                  <p>{disease.symptoms}</p>
                </div>

                <div className="pdp-section pdp-solution-box">
                  <h3><FiCheckCircle size={14} className="check-icon" /> Recommended Treatment</h3>
                  <p>{disease.solution}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
